import { useState, type FormEvent } from 'react';
import { Alert, Button, Card, Field, Input, Select, money, useConfirm } from '@busbook/ui';
import { Page } from '../components/Shell.js';
import { Column, DataTable, Drawer } from '../components/DataTable.js';
import { errorMessage, useResource } from '../lib/useResource.js';
import { api } from '../lib/api.js';

/**
 * Campaigns the platform runs across every tenant (DECISIONS D70).
 *
 * These are PLATFORM-FUNDED, which is not a label but a billing rule: an operator whose
 * passenger uses one of these codes is still billed commission on the fare they would have
 * taken, because the platform bought the discount. The server decides that from who is
 * asking, never from this form.
 *
 * Same editor as the operator's own, deliberately — the two consoles already keep their own
 * Shell, DataTable and Icon, and a campaign is a campaign whoever is paying for it.
 */

interface PromotionRow {
  id: string;
  code: string;
  name: string;
  description: string | null;
  kind: 'PERCENTAGE' | 'FIXED_AMOUNT';
  percentBps: number | null;
  amountCents: number | null;
  currency: string | null;
  usesPerCustomer: number;
  maxRedemptions: number | null;
  grantValidityDays: number | null;
  redeemedCount: number;
  outstandingGrants: number;
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ENDED';
  startsAt: string | null;
  endsAt: string | null;
  referralRole: 'REFERRER' | 'REFEREE' | null;
  tenantId: string | null;
}

interface TenantOption {
  id: string;
  name: string;
}

type Draft = {
  code: string;
  name: string;
  description: string;
  kind: 'PERCENTAGE' | 'FIXED_AMOUNT';
  percent: string;
  amount: string;
  currency: string;
  usesPerCustomer: string;
  maxRedemptions: string;
  grantValidityDays: string;
  status: PromotionRow['status'];
  startsAt: string;
  endsAt: string;
  referralRole: '' | 'REFERRER' | 'REFEREE';
  tenantId: string;
};

const BLANK: Draft = {
  code: '',
  name: '',
  description: '',
  kind: 'PERCENTAGE',
  percent: '5',
  amount: '',
  currency: '',
  usesPerCustomer: '1',
  maxRedemptions: '',
  grantValidityDays: '',
  status: 'DRAFT',
  startsAt: '',
  endsAt: '',
  referralRole: '',
  tenantId: '',
};

function draftOf(row: PromotionRow): Draft {
  return {
    code: row.code,
    name: row.name,
    description: row.description ?? '',
    kind: row.kind,
    // Basis points are how it is stored; percent is how anybody talks about it.
    percent: row.percentBps === null ? '' : String(row.percentBps / 100),
    amount: row.amountCents === null ? '' : String(row.amountCents / 100),
    currency: row.currency ?? '',
    usesPerCustomer: String(row.usesPerCustomer),
    maxRedemptions: row.maxRedemptions === null ? '' : String(row.maxRedemptions),
    grantValidityDays: row.grantValidityDays === null ? '' : String(row.grantValidityDays),
    status: row.status,
    startsAt: row.startsAt ? row.startsAt.slice(0, 10) : '',
    endsAt: row.endsAt ? row.endsAt.slice(0, 10) : '',
    referralRole: row.referralRole ?? '',
    tenantId: row.tenantId ?? '',
  };
}

/** A date input gives a day; the API wants an instant. Midnight UTC, so it means that day. */
function asInstant(day: string): string | undefined {
  return day ? new Date(`${day}T00:00:00.000Z`).toISOString() : undefined;
}

function payloadOf(draft: Draft, isCreate: boolean) {
  return {
    // The API ignores tenantId on update anyway, but the picker is disabled while editing
    // too — retargeting a live campaign would move discounts already granted.
    ...(isCreate && draft.tenantId ? { tenantId: draft.tenantId } : {}),
    code: draft.code.trim(),
    name: draft.name.trim(),
    description: draft.description.trim(),
    kind: draft.kind,
    ...(draft.kind === 'PERCENTAGE'
      ? { percentBps: Math.round(Number(draft.percent || '0') * 100) }
      : {
          // Minor units on the wire, always. A form that posts 12.5 has already lost.
          amountCents: Math.round(Number(draft.amount || '0') * 100),
          currency: draft.currency.trim().toUpperCase(),
        }),
    usesPerCustomer: Number(draft.usesPerCustomer || '1'),
    ...(draft.maxRedemptions ? { maxRedemptions: Number(draft.maxRedemptions) } : {}),
    ...(draft.grantValidityDays.trim()
      ? { grantValidityDays: Number(draft.grantValidityDays) }
      : {}),
    status: draft.status,
    startsAt: asInstant(draft.startsAt),
    endsAt: asInstant(draft.endsAt),
    ...(draft.referralRole ? { referralRole: draft.referralRole } : {}),
  };
}

export function Promotions() {
  const { data, error, loading, reload } = useResource<PromotionRow[]>('/platform/promotions');
  // A failure here leaves the picker with only "Every operator", which is the safe default
  // and the behaviour every existing campaign already has.
  //
  // /platform/tenants is paginated (default page size 20) — without pageSize the picker
  // silently cannot target any operator past the 20th, and the list column below renders
  // their campaigns as "Unknown operator" instead of erroring. See Tenants.tsx for the same
  // fix. No status filter: this same resource also feeds the list column's "For:" name, and
  // a launch campaign is usually aimed at an ONBOARDING tenant in the first place.
  const tenants = useResource<TenantOption[]>('/platform/tenants?pageSize=100');
  const [editing, setEditing] = useState<PromotionRow | 'new' | null>(null);
  const { ask, dialog } = useConfirm();
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const tenantName = (id: string) =>
    tenants.data?.find((t) => t.id === id)?.name ?? 'Unknown operator';

  async function end(row: PromotionRow) {
    const yes = await ask({
      title: `End “${row.name}”?`,
      // Not deleted: grants already handed out still have to resolve to something, and a
      // redeemed promotion is part of the record of what passengers were charged.
      body: 'No new passengers can claim it. Anyone already holding it keeps what they have.',
      confirmLabel: 'End it',
      destructive: true,
    });
    if (!yes) return;
    await api.delete(`/platform/promotions/${row.id}`);
    reload();
  }

  const columns: Column<PromotionRow>[] = [
    {
      key: 'code',
      header: 'Code',
      render: (row) => (
        <span className="tabular font-semibold">
          {row.code}
          {row.referralRole && (
            <span className="ml-2 text-xs font-normal" style={{ color: 'var(--color-muted)' }}>
              {row.referralRole === 'REFERRER' ? 'referrer reward' : 'joining offer'}
            </span>
          )}
        </span>
      ),
    },
    { key: 'name', header: 'Name', render: (row) => row.name },
    {
      key: 'tenant',
      header: 'For',
      render: (row) => (row.tenantId ? tenantName(row.tenantId) : 'Every operator'),
    },
    {
      key: 'value',
      header: 'Value',
      render: (row) =>
        row.kind === 'PERCENTAGE'
          ? `${(row.percentBps ?? 0) / 100}%`
          : row.amountCents !== null && row.currency
            ? money(row.amountCents, row.currency)
            : '—',
    },
    {
      key: 'uses',
      header: 'Per passenger',
      render: (row) => (row.usesPerCustomer === 1 ? '1 ride' : `${row.usesPerCustomer} rides`),
    },
    {
      key: 'claimed',
      header: 'Claimed',
      render: (row) => (
        <span className="tabular">
          {row.redeemedCount}
          {row.maxRedemptions !== null ? ` / ${row.maxRedemptions}` : ''}
        </span>
      ),
    },
    {
      key: 'outstanding',
      // What is still promised and not yet given: the number that matters when deciding
      // whether ending a campaign is safe.
      header: 'Outstanding',
      render: (row) => <span className="tabular">{row.outstandingGrants}</span>,
    },
    { key: 'status', header: 'Status', render: (row) => row.status },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <span className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={() => setEditing(row)}>
            Edit
          </Button>
          {row.status !== 'ENDED' && (
            <Button size="sm" variant="ghost" onClick={() => void end(row)}>
              End
            </Button>
          )}
        </span>
      ),
    },
  ];

  return (
    <Page
      title="Promotions"
      description="Campaigns you run across every tenant. You fund the discount, so operators are still billed commission on the full fare."
      actions={
        <Button size="sm" onClick={() => setEditing('new')}>
          New promotion
        </Button>
      }
    >
      {dialog}
      {message && (
        <div className="mb-4">
          <Alert tone={message.tone}>{message.text}</Alert>
        </div>
      )}
      {error ? (
        <Alert tone="error">{errorMessage(error)}</Alert>
      ) : (
        <Card>
          <DataTable
            columns={columns}
            rows={data ?? []}
            loading={loading}
            empty={{
              title: 'No platform campaigns yet',
              description: 'Create one and every tenant’s passengers can use its code.',
            }}
            rowKey={(row) => row.id}
          />
        </Card>
      )}

      {editing && (
        <PromotionDrawer
          row={editing === 'new' ? null : editing}
          tenants={tenants.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={(text) => {
            setEditing(null);
            setMessage({ tone: 'success', text });
            reload();
          }}
        />
      )}
    </Page>
  );
}

function PromotionDrawer({
  row,
  tenants,
  onClose,
  onSaved,
}: {
  row: PromotionRow | null;
  tenants: TenantOption[];
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => (row ? draftOf(row) : BLANK));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set(patch: Partial<Draft>) {
    setDraft((current) => ({ ...current, ...patch }));
    setError(null);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = payloadOf(draft, !row);
      if (row) await api.put(`/platform/promotions/${row.id}`, body);
      else await api.post('/platform/promotions', body);
      onSaved(row ? 'Promotion updated.' : 'Promotion created.');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer open title={row ? `Edit ${row.code}` : 'New promotion'} onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field
          label="For"
          htmlFor="p-tenant"
          hint="Leave as every operator to run this across the whole product."
        >
          <Select
            id="p-tenant"
            value={draft.tenantId}
            onChange={(e) => set({ tenantId: e.target.value })}
            disabled={Boolean(row)}
          >
            <option value="">Every operator</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Code" htmlFor="p-code" hint="What a passenger types. Letters, numbers and hyphens.">
          <Input
            id="p-code"
            className="tabular uppercase"
            value={draft.code}
            onChange={(e) => set({ code: e.target.value.toUpperCase() })}
            required
          />
        </Field>

        <Field label="Name" htmlFor="p-name" hint="Shown to passengers on the offers screen.">
          <Input id="p-name" value={draft.name} onChange={(e) => set({ name: e.target.value })} required />
        </Field>

        <Field label="Description" htmlFor="p-desc">
          <Input
            id="p-desc"
            value={draft.description}
            onChange={(e) => set({ description: e.target.value })}
          />
        </Field>

        <Field label="Discount" htmlFor="p-kind">
          <Select
            id="p-kind"
            value={draft.kind}
            onChange={(e) => set({ kind: e.target.value as Draft['kind'] })}
          >
            <option value="PERCENTAGE">A percentage off</option>
            <option value="FIXED_AMOUNT">A fixed amount off</option>
          </Select>
        </Field>

        {draft.kind === 'PERCENTAGE' ? (
          <Field label="Percent off" htmlFor="p-percent" hint="100 makes the ride free.">
            <Input
              id="p-percent"
              type="number"
              min="0.01"
              max="100"
              step="0.01"
              value={draft.percent}
              onChange={(e) => set({ percent: e.target.value })}
              required
            />
          </Field>
        ) : (
          <div className="grid grid-cols-[1fr_7rem] gap-3">
            <Field label="Amount off" htmlFor="p-amount">
              <Input
                id="p-amount"
                type="number"
                min="0.01"
                step="0.01"
                value={draft.amount}
                onChange={(e) => set({ amount: e.target.value })}
                required
              />
            </Field>
            <Field label="Currency" htmlFor="p-currency" hint="e.g. CDF">
              <Input
                id="p-currency"
                maxLength={3}
                className="uppercase"
                value={draft.currency}
                onChange={(e) => set({ currency: e.target.value.toUpperCase() })}
                required
              />
            </Field>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Rides per passenger"
            htmlFor="p-uses"
            hint="5 means “next 5 rides”."
          >
            <Input
              id="p-uses"
              type="number"
              min="1"
              max="100"
              value={draft.usesPerCustomer}
              onChange={(e) => set({ usesPerCustomer: e.target.value })}
              required
            />
          </Field>
          <Field label="Total claims" htmlFor="p-max" hint="Blank means no limit.">
            <Input
              id="p-max"
              type="number"
              min="1"
              value={draft.maxRedemptions}
              onChange={(e) => set({ maxRedemptions: e.target.value })}
            />
          </Field>
          <Field
            label="Reward valid for"
            htmlFor="p-validity"
            hint="Days from when somebody earns it. Leave blank and it lasts as long as the campaign."
          >
            <Input
              id="p-validity"
              type="number"
              min={1}
              max={730}
              value={draft.grantValidityDays}
              onChange={(e) => set({ grantValidityDays: e.target.value })}
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Starts" htmlFor="p-starts">
            <Input
              id="p-starts"
              type="date"
              value={draft.startsAt}
              onChange={(e) => set({ startsAt: e.target.value })}
            />
          </Field>
          <Field label="Ends" htmlFor="p-ends">
            <Input
              id="p-ends"
              type="date"
              value={draft.endsAt}
              onChange={(e) => set({ endsAt: e.target.value })}
            />
          </Field>
        </div>

        <Field label="Status" htmlFor="p-status" hint="Only ACTIVE codes can be claimed.">
          <Select
            id="p-status"
            value={draft.status}
            onChange={(e) => set({ status: e.target.value as Draft['status'] })}
          >
            <option value="DRAFT">Draft</option>
            <option value="ACTIVE">Active</option>
            <option value="PAUSED">Paused</option>
            <option value="ENDED">Ended</option>
          </Select>
        </Field>

        <Field
          label="Part of refer and earn"
          htmlFor="p-referral"
          hint="A referral arm is awarded automatically, never typed in as a code."
        >
          <Select
            id="p-referral"
            value={draft.referralRole}
            onChange={(e) => set({ referralRole: e.target.value as Draft['referralRole'] })}
          >
            <option value="">No — an ordinary code</option>
            <option value="REFEREE">Joining offer, for the new passenger</option>
            <option value="REFERRER">Reward, for whoever invited them</option>
          </Select>
        </Field>

        {error && <Alert tone="error">{error}</Alert>}

        <div className="flex gap-2">
          <Button type="submit" loading={busy}>
            {row ? 'Save' : 'Create'}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Drawer>
  );
}
